"use client";

import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import ExtensionInstallDialog from "@/components/ui/extension-install-dialog";
import { useBilling } from "@/contexts/BillingContext";
import { getPlanTypeLabel, getCreditsTotal } from "@/lib/utils";
import { toast } from "sonner";
import { Calendar, Star, Zap, BookOpen, FileText, Chrome, ExternalLink, ArrowRight, Plus } from "lucide-react";
import { format } from "date-fns";
import { detectExtensionInstalled, getChromeWebStoreUrl } from "@/lib/extension";
import Link from "next/link";
import { PlanType } from "@/types/auth";
import { TopupDialog } from "@/components/dashboard/TopupDialog";
import posthog from "posthog-js";

export default function WebAppDashboard() {
  const { billingData } = useBilling();

  const isFreeUser = billingData?.planType === PlanType.FREE;
  const planLabel = billingData ? getPlanTypeLabel(billingData.planType) : "";
  const totalCredits = billingData ? getCreditsTotal(billingData.planType) : 0;
  const creditsUsed = billingData ? totalCredits - billingData.creditsRemaining : 0;

  const [isExtensionInstalled, setIsExtensionInstalled] = useState<boolean | null>(null);
  const [topupDialogOpen, setTopupDialogOpen] = useState(false);

  useEffect(() => {
    setIsExtensionInstalled(detectExtensionInstalled());
  }, []);

  return (
    <>
      <ExtensionInstallDialog />
      
      <div className="mb-6">
        <div className="mb-4">
          <h2 className="text-lg font-semibold">Account Overview</h2>
          <p className="text-muted-foreground text-sm">
            Your plan, credits, and quick actions to get started.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Current Plan Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Star className="h-5 w-5" />
                Current Plan
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {billingData ? (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{planLabel} Plan</h3>
                      <p className="text-sm text-muted-foreground">
                        {totalCredits} credits per month
                      </p>
                    </div>
                    <Badge 
                      variant={billingData.billingStatus === "active" ? "default" : "secondary"}
                    >
                      {billingData.billingStatus === "active" ? "Active" : billingData.billingStatus}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Credits remaining</span>
                      <span className="font-medium">
                        {Math.round(billingData.creditsRemaining * 100) / 100} / {totalCredits}
                      </span>
                    </div>
                    <Progress value={Math.round((billingData.creditsRemaining / totalCredits) * 100)} className="h-2" />
                    <p className="text-xs text-muted-foreground">
                      {creditsUsed === 0 
                        ? "No credits used yet in this billing period" 
                        : `${Math.round(creditsUsed * 100) / 100} credits used in this billing period`
                      }
                    </p>
                  </div>

                  {!isFreeUser && billingData.currentPeriodEnd && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      <span>
                        Credits reset on {format(new Date(billingData.currentPeriodEnd), "MMM d, yyyy")}
                      </span>
                    </div>
                  )}

                  {!isFreeUser && (
                    <Button
                      onClick={() => {
                        posthog.capture("topup_button_clicked", { source: "dashboard_plan_card" });
                        setTopupDialogOpen(true);
                      }}
                      variant="outline"
                      className="w-full cursor-pointer"
                    >
                      <Plus className="h-4 w-4" />
                      Buy More Credits
                    </Button>
                  )}

                  {isFreeUser && (
                    <div className="flex flex-col gap-2">
                      <Button
                        onClick={() => {
                          posthog.capture("topup_button_clicked", { source: "dashboard_plan_card" });
                          setTopupDialogOpen(true);
                        }}
                        className="w-full cursor-pointer"
                      >
                        <Plus className="h-4 w-4" />
                        Buy Credits
                      </Button>
                      <Link href="/pricing">
                        <Button variant="outline" className="w-full cursor-pointer">
                          <Star fill="currentColor" className="h-4 w-4 mr-2 text-yellow-500" />
                          Upgrade Plan
                        </Button>
                      </Link>
                    </div>
                  )}
                </>
              ) : (
                <div className="space-y-4">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-2 w-full" />
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 w-40" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Quick Actions
              </CardTitle>
              <CardDescription>
                Get started with FillApp browser agent
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {!isExtensionInstalled ? (
                <>
                  <div className="space-y-3">
                    <button 
                      onClick={() => {
                        const url = getChromeWebStoreUrl();
                        window.open(url, "_blank");
                        toast.success("Opening Chrome Web Store…");
                      }}
                      className="w-full p-3 rounded-lg border bg-muted/20 hover:bg-muted/30 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <Chrome className="h-5 w-5 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Install Extension</p>
                          <p className="text-xs text-muted-foreground">Add FillApp to your browser</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </button>
                    
                    <Link 
                      href="/dashboard/snippets"
                      className="block w-full p-3 rounded-lg border bg-muted/20 hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Create Your First Snippet</p>
                          <p className="text-xs text-muted-foreground">Save reusable form data</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </Link>
                    
                    <Link 
                      href="/blog"
                      className="block w-full p-3 rounded-lg border bg-muted/20 hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <BookOpen className="h-5 w-5 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Read Use Cases</p>
                          <p className="text-xs text-muted-foreground">See how others save time</p>
                        </div>
                        <ExternalLink className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-3">
                    <Link 
                      href="/dashboard/snippets" 
                      className="block p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Manage Snippets</p>
                          <p className="text-xs text-muted-foreground">Edit your saved form data</p>
                        </div>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </Link>
                    
                    <Link 
                      href="/blog"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <BookOpen className="h-5 w-5 text-primary" />
                        <div className="flex-1">
                          <p className="font-medium text-sm">Learn Best Practices</p>
                          <p className="text-xs text-muted-foreground">Productivity tips & tricks</p>
                        </div>
                        <ExternalLink className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </Link>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <TopupDialog open={topupDialogOpen} onOpenChange={setTopupDialogOpen} />
    </>
  );
}
